import { Injectable } from '@angular/core';
import { RouterState } from '@angular/router';
import {
  BrandIReckonInRoomCredentialsAdd,
  FrontDeskIReckonInRoomCredentials,
  IReckonInRoomCredentials,
  IntegrationIReckonInRoomCredentialsService,
  LocationIReckonInRoomCredentials,
  RoomIReckonInRoomCredentials,
} from '@era/locations-sdk';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { EMPTY, Observable, concat, of } from 'rxjs';
import { filter, first, map, mergeMap } from 'rxjs/operators';
import { Category } from '../router/category';
import {
  CategoryInRoomCredentialsApiActions,
  CategoryInRoomCredentialsResolverActions,
  CategoryInRoomCredentialsViewActions,
} from './category-in-room-credentials.actions';
import { selectInRoomCredentialsLinkedToCategory } from './category-in-room-credentials.selector';
import {
  InRoomCredentialsConfiguration,
  inRoomCredentialsConfigurationFrom,
} from './category-in-room-credentials.state';
import { safeMap } from '../effects.helpers';

@Injectable()
export class CategoryInRoomCredentialsEffects {
  constructor(
    private readonly actions$: Actions,
    private readonly api: IntegrationIReckonInRoomCredentialsService,
    private readonly routeStore: Store<RouterState>
  ) {}

  // Our API supports 0-N relations between categories and configurations. Our UI supports 0-1 relations. So we always pick
  // the first relation from the API when loading them.
  loadCategoryInRoomCredentialsEffect$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(CategoryInRoomCredentialsResolverActions.categoryCredentialsRequested),
      mergeMap((action) =>
        safeMap(
          this.loadCategoryInRoomCredentials(action.category).pipe(
            filter((configurations) => configurations.length > 0),
            map((configurations) => configurations[0])
          ),
          (configuration) => CategoryInRoomCredentialsApiActions.categoryCredentialsLoaded({ configuration }),
          (error) => CategoryInRoomCredentialsApiActions.categoryCredentialsLoadedError({ error })
        )
      )
    );
  });

  linkInRoomCredentialsToCategoryEffect$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(CategoryInRoomCredentialsViewActions.credentialsSelected),
      mergeMap((action) => {
        return this.routeStore.select(selectInRoomCredentialsLinkedToCategory(action.category)).pipe(
          filter(
            (existingCredentials) => (existingCredentials ? existingCredentials.id !== action.credentials.id : true) // has change?
          ),
          first(),
          mergeMap((existingCredentials) => {
            // Delete the original configuration
            const delete$: Observable<Action> = existingCredentials
              ? this.delete$(action.category, {
                  type: action.category.categoryType,
                  id: action.category.categoryId,
                  iReckonInRoomCredentialsId: existingCredentials.id,
                })
              : EMPTY;

            // Add the new configuration
            const create$: Observable<Action> = this.create$(action.category, action.credentials);

            return concat(delete$, create$);
          })
        );
      })
    );
  });

  removeLinkedInRoomCredentialsFromCategoryEffect$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(CategoryInRoomCredentialsViewActions.credentialsDeselected),
      mergeMap((action) => {
        return this.routeStore.select(selectInRoomCredentialsLinkedToCategory(action.category)).pipe(
          filter((existingCredentials) => !!existingCredentials),
          first(),
          mergeMap((existingCredentials) =>
            this.delete$(action.category, {
              type: action.category.categoryType,
              id: action.category.categoryId,
              iReckonInRoomCredentialsId: existingCredentials.id,
            })
          )
        );
      })
    );
  });

  private loadCategoryInRoomCredentials(category: Category): Observable<InRoomCredentialsConfiguration[]> {
    switch (category.categoryType) {
      case 'Brand':
        return this.api.getBrandIReckonInRoomCredentials(null, category.categoryId).pipe(
          filter((configurations) => configurations.length > 0),
          map((configurations) => configurations.map((config) => inRoomCredentialsConfigurationFrom(config)))
        );
      case 'Location':
        return this.api.getLocationIReckonInRoomCredentials(null, category.categoryId).pipe(
          filter((configurations) => configurations.length > 0),
          map((configurations) => configurations.map((config) => inRoomCredentialsConfigurationFrom(config)))
        );
      case 'Room':
        return this.api.getRoomIReckonInRoomCredentials(null, category.categoryId).pipe(
          filter((configurations) => configurations.length > 0),
          map((configurations) => configurations.map((config) => inRoomCredentialsConfigurationFrom(config)))
        );
      case 'FrontDesk':
        return this.api.getFrontDeskIReckonInRoomCredentials(null, category.categoryId).pipe(
          filter((configurations) => configurations.length > 0),
          map((configurations) => configurations.map((config) => inRoomCredentialsConfigurationFrom(config)))
        );
      default:
        return of([]);
    }
  }

  private readonly create$ = (category: Category, credentials: IReckonInRoomCredentials): Observable<Action> => {
    switch (category.categoryType) {
      case 'Brand': {
        const brandConfiguration: InRoomCredentialsConfiguration = {
          type: category.categoryType,
          id: category.categoryId,
          iReckonInRoomCredentialsId: credentials.id,
        };

        const brandCredentials: BrandIReckonInRoomCredentialsAdd = {
          brandUniqueId: category.categoryId,
          iReckonInRoomCredentialsId: credentials.id,
        };

        return safeMap(
          this.api.addBrandIReckonInRoomCredentials(brandCredentials),
          (configuration) =>
            CategoryInRoomCredentialsApiActions.categoryCredentialsAdded({
              configuration: inRoomCredentialsConfigurationFrom(configuration),
            }),
          (error) =>
            CategoryInRoomCredentialsApiActions.categoryCredentialsAddedError({
              configuration: brandConfiguration,
              error,
            })
        );
      }
      case 'Location': {
        const locationConfiguration: InRoomCredentialsConfiguration = {
          type: 'Location',
          id: category.categoryId,
          iReckonInRoomCredentialsId: credentials.id,
        };

        const locationCredentials: LocationIReckonInRoomCredentials | BrandIReckonInRoomCredentialsAdd = {
          locationUniqueId: category.categoryId,
          iReckonInRoomCredentials: credentials,
          iReckonInRoomCredentialsId: credentials.id,
        };

        return safeMap(
          this.api.addLocationIReckonInRoomCredentials(locationCredentials),
          (configuration) =>
            CategoryInRoomCredentialsApiActions.categoryCredentialsAdded({
              configuration: inRoomCredentialsConfigurationFrom(configuration),
            }),
          (error) =>
            CategoryInRoomCredentialsApiActions.categoryCredentialsAddedError({
              configuration: locationConfiguration,
              error,
            })
        );
      }
      case 'Room': {
        const roomConfiguration: InRoomCredentialsConfiguration = {
          type: 'Room',
          id: category.categoryId,
          iReckonInRoomCredentialsId: credentials.id,
        };

        const roomCredentials: RoomIReckonInRoomCredentials | BrandIReckonInRoomCredentialsAdd = {
          roomUniqueId: category.categoryId,
          iReckonInRoomCredentials: credentials,
          iReckonInRoomCredentialsId: credentials.id,
        };

        return safeMap(
          this.api.addRoomIReckonInRoomCredentials(roomCredentials),
          (configuration) =>
            CategoryInRoomCredentialsApiActions.categoryCredentialsAdded({
              configuration: inRoomCredentialsConfigurationFrom(configuration),
            }),
          (error) =>
            CategoryInRoomCredentialsApiActions.categoryCredentialsAddedError({
              configuration: roomConfiguration,
              error,
            })
        );
      }
      case 'FrontDesk': {
        const frontDeskConfiguration: InRoomCredentialsConfiguration = {
          type: 'FrontDesk',
          id: category.categoryId,
          iReckonInRoomCredentialsId: credentials.id,
        };

        const frontDeskCredentials: FrontDeskIReckonInRoomCredentials | BrandIReckonInRoomCredentialsAdd = {
          frontDeskUniqueId: category.categoryId,
          iReckonInRoomCredentials: credentials,
          iReckonInRoomCredentialsId: credentials.id,
        };

        return safeMap(
          this.api.addFrontDeskIReckonInRoomCredentials(frontDeskCredentials),
          (configuration) =>
            CategoryInRoomCredentialsApiActions.categoryCredentialsAdded({
              configuration: inRoomCredentialsConfigurationFrom(configuration),
            }),
          (error) =>
            CategoryInRoomCredentialsApiActions.categoryCredentialsAddedError({
              configuration: frontDeskConfiguration,
              error,
            })
        );
      }
      default:
        return EMPTY;
    }
  };

  private readonly delete$ = (
    category: Category,
    configuration: InRoomCredentialsConfiguration
  ): Observable<Action> => {
    switch (category.categoryType) {
      case 'Brand':
        return safeMap(
          this.api.deleteBrandIReckonInRoomCredentials(configuration.iReckonInRoomCredentialsId, category.categoryId),
          () => CategoryInRoomCredentialsApiActions.categoryCredentialsDeleted({ configuration }),
          (error) => CategoryInRoomCredentialsApiActions.categoryCredentialsDeletedError({ configuration, error })
        );
      case 'Location':
        return safeMap(
          this.api.deleteLocationIReckonInRoomCredentials(
            configuration.iReckonInRoomCredentialsId,
            category.categoryId
          ),
          () => CategoryInRoomCredentialsApiActions.categoryCredentialsDeleted({ configuration }),
          (error) => CategoryInRoomCredentialsApiActions.categoryCredentialsDeletedError({ configuration, error })
        );
      case 'Room':
        return safeMap(
          this.api.deleteRoomIReckonInRoomCredentials(configuration.iReckonInRoomCredentialsId, category.categoryId),
          () => CategoryInRoomCredentialsApiActions.categoryCredentialsDeleted({ configuration }),
          (error) => CategoryInRoomCredentialsApiActions.categoryCredentialsDeletedError({ configuration, error })
        );
      case 'FrontDesk':
        return safeMap(
          this.api.deleteFrontDeskIReckonInRoomCredentials(
            configuration.iReckonInRoomCredentialsId,
            category.categoryId
          ),
          () => CategoryInRoomCredentialsApiActions.categoryCredentialsDeleted({ configuration }),
          (error) => CategoryInRoomCredentialsApiActions.categoryCredentialsDeletedError({ configuration, error })
        );
      default:
        return EMPTY;
    }
  };
}
